import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Appointment, Company, FollowUpStatusItem } from "../../../types";
import { appointmentsApi } from "../../../services/appointmentsApi";
import {
  getStatusBadgeColor,
  translateStatus,
  getPaymentMethodColor,
  getCarEffortColor,
  getCarEffortTextColor,
  appointmentSpansMultipleDays,
  toAppointmentDateOnly,
} from "../../../utils/appointmentUtils";
import { ClientCarsModal } from "./ClientCarsModal";
import { CreateAppointmentModal } from "./CreateAppointmentModal";
import { UpdatePaymentModal } from "./UpdatePaymentModal";
import { AppointmentNotesModal } from "./AppointmentNotesModal";
import { getServiceIcon } from "../../../utils/themeUtils";
import { isBirthdayToday, formatTime, formatDateShort } from "../../../utils/dateUtils";
import { openGoogleMaps } from "../../../utils/googleMapsUtils";
import { Icons } from "../../shared/Icons";
import { CarBrandLogo } from "../../shared/CarBrandLogo";
import { ActionButton } from "../../shared";
import {
  CarExpiredFollowUpReminder,
  getFollowUpItemsForAppointment,
} from "./CarExpiredFollowUpReminder";

interface AppointmentsGridViewProps {
  appointments: Appointment[];
  company: Company | null;
  isAppointmentFinished: (appointment: Appointment) => boolean;
  formatCurrency: (price: string) => string;
  onRefresh: () => void;
  onCancel?: (appointment: Appointment) => void;
  onReactivate?: (appointment: Appointment) => void;
  /** When true (professional + car): only Service, Car, Status; card clickable to view detail. No edit. */
  minimalView?: boolean;
  followUpByClientId?: Map<number, FollowUpStatusItem[]>;
  onFollowUpScheduled?: () => void;
}

export const AppointmentsGridView: React.FC<AppointmentsGridViewProps> = ({
  appointments,
  company,
  isAppointmentFinished,
  formatCurrency,
  onRefresh,
  onCancel,
  onReactivate,
  minimalView = false,
  followUpByClientId,
  onFollowUpScheduled,
}) => {
  const navigate = useNavigate();
  const isCarCompany = company?.category === "car";

  const renderDateRange = (appointment: Appointment) => {
    if (!isCarCompany || !appointmentSpansMultipleDays(appointment)) {
      return null;
    }

    return (
      <p className="text-xs font-medium text-sky-700 flex items-center gap-1">
        <Icons.CalendarMonth sx={{ fontSize: 14 }} />
        {formatDateShort(toAppointmentDateOnly(appointment.appointmentDate))}{" "}
        até{" "}
        {formatDateShort(toAppointmentDateOnly(appointment.appointmentEnd))}
      </p>
    );
  };

  const renderDeliveryTimes = (appointment: Appointment, onEdit?: () => void) => {
    if (!appointment.deliveryStartTime && !appointment.deliveryEndTime) {
      return null;
    }

    const start = appointment.deliveryStartTime
      ? formatTime(appointment.deliveryStartTime)
      : null;
    const end = appointment.deliveryEndTime
      ? formatTime(appointment.deliveryEndTime)
      : null;
    const range =
      start && end ? `${start} – ${end}` : start || end || "";

    return (
      <div className="bg-sky-50 dark:bg-sky-950/40 rounded-lg p-3 border border-sky-100 dark:border-sky-800">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-sky-800 dark:text-sky-300 mb-1">
              Horário de entrega
            </p>
            <p className="text-sm text-gray-700 dark:text-slate-200 flex items-center gap-1.5">
              <Icons.Schedule sx={{ fontSize: 16 }} className="text-sky-600" />
              {range}
            </p>
          </div>
          {onEdit && (
            <button
              type="button"
              onClick={onEdit}
              className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded transition-colors flex-shrink-0"
              title="Editar observações e entrega"
            >
              <Icons.Edit sx={{ fontSize: 16 }} />
            </button>
          )}
        </div>
      </div>
    );
  };

  const openNotesModal = (appointment: Appointment) => {
    setAppointmentForNotes(appointment);
    setNotesModalOpen(true);
  };
  const [clientCarsModalOpen, setClientCarsModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] =
    useState<Appointment | null>(null);
  const [updatingAppointment, setUpdatingAppointment] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [appointmentToEdit, setAppointmentToEdit] = useState<Appointment | null>(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [appointmentForPayment, setAppointmentForPayment] = useState<Appointment | null>(null);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [appointmentForNotes, setAppointmentForNotes] = useState<Appointment | null>(null);

  const getStatusHeaderColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "scheduled":
        return "bg-gray-50/80 dark:bg-slate-900/50 border-gray-200 dark:border-slate-700";
      case "confirmed":
        return "bg-gray-50/80 dark:bg-slate-900/50 border-gray-200 dark:border-slate-700";
      case "in_progress":
        return "bg-blue-50/80 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800";
      case "completed":
        return "bg-green-50/80 dark:bg-green-950/40 border-green-200 dark:border-green-800";
      case "cancelled":
        return "bg-red-50/80 dark:bg-red-950/40 border-red-200 dark:border-red-800";
      default:
        return "bg-gray-50/80 dark:bg-slate-900/50 border-gray-200 dark:border-slate-700";
    }
  };

  const getStatusBorderColor = (status: string, finished: boolean) => {
    if (finished) {
      return "opacity-75 border-gray-300 dark:border-slate-600";
    }

    switch (status.toLowerCase()) {
      case "scheduled":
        return "border-gray-300 dark:border-slate-600 hover:border-gray-400 dark:hover:border-slate-500";
      case "confirmed":
        return "border-gray-300 dark:border-slate-600 hover:border-gray-400 dark:hover:border-slate-500";
      case "in_progress":
        return "border-blue-300 dark:border-blue-700 hover:border-blue-400 dark:hover:border-blue-500";
      case "completed":
        return "border-green-300 dark:border-green-700 hover:border-green-400 dark:hover:border-green-500";
      case "cancelled":
        return "border-red-300 dark:border-red-700 hover:border-red-400 dark:hover:border-red-500";
      default:
        return "border-gray-300 dark:border-slate-600 hover:border-gray-400 dark:hover:border-slate-500";
    }
  };

  // Minimal view: beautiful professional layout — click card to open appointment detail
  if (minimalView) {
    const getStatusAccent = (status: string) => {
      switch (status?.toLowerCase()) {
        case "scheduled":
        case "confirmed":
          return "border-l-slate-400 dark:!border-l-slate-500 bg-slate-50/40 dark:bg-slate-800";
        case "in_progress":
          return "border-l-blue-500 dark:!border-l-blue-400 bg-blue-50/40 dark:bg-slate-800";
        case "completed":
          return "border-l-emerald-500 dark:!border-l-emerald-400 bg-emerald-50/40 dark:bg-slate-800";
        case "cancelled":
          return "border-l-rose-400 dark:!border-l-rose-400 bg-rose-50/30 dark:bg-slate-800";
        default:
          return "border-l-gray-300 dark:!border-l-slate-600 bg-white dark:bg-slate-800";
      }
    };
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5 p-4 sm:p-6">
        {appointments.map((appointment) => (
          <div
            key={appointment.id}
            role="button"
            tabIndex={0}
            onClick={() => navigate(`${appointment.id}`)}
            onKeyDown={(e) => e.key === "Enter" && navigate(`${appointment.id}`)}
            className={`group relative rounded-2xl border-y border-r border-gray-100 dark:border-slate-700 border-l-4 shadow-sm hover:shadow-lg hover:scale-[1.02] active:scale-[0.99] transition-all duration-200 cursor-pointer overflow-hidden ${getStatusAccent(
              appointment.status
            )}`}
          >
            <div className="p-5 sm:p-6">
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-11 h-11 rounded-xl bg-white/80 dark:bg-slate-700 shadow-sm border border-gray-100 dark:border-slate-600 flex items-center justify-center flex-shrink-0 text-xl">
                    {getServiceIcon(company)}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 dark:text-white truncate text-base">
                      {appointment.service?.name || "Serviço Desconhecido"}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">Serviço</p>
                    {renderDateRange(appointment)}
                  </div>
                </div>
                <span
                  className={`flex-shrink-0 px-2.5 py-1 rounded-lg text-xs font-semibold border shadow-sm ${getStatusBadgeColor(
                    appointment.status
                  )}`}
                >
                  {translateStatus(appointment.status)}
                </span>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/admin/clients/${appointment.client.id}/cars`);
                }}
                className={`w-full flex items-center gap-2.5 rounded-xl py-2.5 px-3 text-left hover:opacity-90 transition-opacity ${
                  appointment.clientCar
                    ? getCarEffortColor(appointment.clientCar.effort)
                    : "bg-white/60 dark:bg-slate-700/80 text-gray-700 dark:text-slate-200"
                }`}
                title={appointment.clientCar ? "Ver veículos do cliente" : undefined}
              >
                <CarBrandLogo
                  brand={appointment.clientCar?.brand}
                  className="flex-shrink-0"
                />
                <p className="text-sm font-medium truncate min-w-0">
                  {appointment.clientCar
                    ? `${appointment.clientCar.model} — ${appointment.clientCar.plate}`
                    : "Veículo não informado"}
                </p>
              </button>
              {appointment.notes && (
                <div className="mt-3 rounded-xl bg-white/70 dark:bg-slate-900/50 border border-gray-100 dark:border-slate-600 px-3 py-2">
                  <p className="text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">Observações</p>
                  <p className="text-sm text-gray-700 dark:text-slate-200 line-clamp-2">{appointment.notes}</p>
                </div>
              )}
              {isCarCompany && (
                <div className="mt-3">
                  {renderDeliveryTimes(appointment)}
                </div>
              )}
              {appointment.client.address && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    openGoogleMaps(appointment.client.address!);
                  }}
                  className="mt-3 w-full flex items-center gap-2.5 rounded-xl py-2.5 px-3 border border-gray-100 dark:border-slate-600 bg-white/60 dark:bg-slate-900/40 text-left hover:bg-blue-50 dark:hover:bg-slate-700 hover:border-blue-200 dark:hover:border-slate-500 transition-colors"
                  title="Abrir endereço no Google Maps"
                >
                  <span className="text-base flex-shrink-0">📍</span>
                  <p className="text-sm text-gray-700 dark:text-slate-200 truncate min-w-0">
                    {appointment.client.address}
                  </p>
                </button>
              )}
              <div className="mt-4 flex items-center justify-end gap-1 text-xs font-medium text-gray-400 dark:text-slate-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                <span>Ver detalhes</span>
                <span className="group-hover:translate-x-0.5 transition-transform">→</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
        {appointments.map((appointment) => {
          const finished = isAppointmentFinished(appointment);
          return (
            <div
              key={appointment.id}
              className={`bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden ${getStatusBorderColor(
                appointment.status,
                finished
              )}`}
            >
              {/* Header */}
              <div
                className={`p-4 border-b ${getStatusHeaderColor(
                  appointment.status
                )}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {appointment.clientCar?.brand ? (
                      <CarBrandLogo brand={appointment.clientCar.brand} />
                    ) : null}
                    <div className="min-w-0">
                      {appointment.service?.type === "day" ? (
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                          Agendamento do Dia
                        </h3>
                      ) : (
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                          {appointment.startTime}
                        </h3>
                      )}
                      {appointment.clientCar ? (
                        <button
                          onClick={() => navigate(`${appointment.id}`)}
                          className={`text-sm font-medium hover:underline cursor-pointer text-left truncate ${
                            appointment.clientCar.effort === "Leve"
                              ? "text-green-600 hover:text-green-800"
                              : appointment.clientCar.effort === "Médio"
                              ? "text-yellow-600 hover:text-yellow-800"
                              : appointment.clientCar.effort === "Pesado"
                              ? "text-red-600 hover:text-red-800"
                              : "text-blue-600 hover:text-blue-800"
                          }`}
                          title={appointment.clientCar.effort ? `Esforço: ${appointment.clientCar.effort}` : undefined}
                        >
                          {appointment.clientCar.model} - {appointment.clientCar.plate}
                        </button>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-slate-400">Sem veículo</p>
                      )}
                      {renderDateRange(appointment)}
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadgeColor(
                      appointment.status
                    )}`}
                  >
                    {translateStatus(appointment.status)}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 space-y-3">
                {/* Service */}
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 dark:text-blue-300 text-sm">
                      {getServiceIcon(company)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0 flex items-center gap-1.5">
                    <p className="font-semibold text-gray-900 dark:text-white truncate">
                      {appointment.service?.name || "Serviço Desconhecido"}
                    </p>
                    <CarExpiredFollowUpReminder
                      items={getFollowUpItemsForAppointment(
                        followUpByClientId,
                        appointment.client.id,
                        appointment.clientCar?.id ?? appointment.clientCarId,
                        isCarCompany,
                      )}
                      clientId={appointment.client.id}
                      clientName={appointment.client.name}
                      clientPhone={appointment.client.phoneNumber}
                      clientCarId={
                        appointment.clientCar?.id ?? appointment.clientCarId
                      }
                      carLabel={
                        appointment.clientCar
                          ? `${appointment.clientCar.model} · ${appointment.clientCar.plate}`
                          : undefined
                      }
                      onScheduled={onFollowUpScheduled}
                    />
                  </div>
                </div>

                {/* Professional */}
                {appointment.professionalName && (
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-purple-100 dark:bg-purple-900/50 rounded-full flex items-center justify-center">
                      <span className="text-purple-600 dark:text-purple-300 text-sm">👨‍💼</span>
                    </div>
                    <p className="text-gray-700 dark:text-slate-300">
                      {appointment.professionalName}
                    </p>
                  </div>
                )}

                {/* Client */}
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icons.Person sx={{ fontSize: 16 }} className="text-green-600 dark:text-green-400" />
                  </div>
                  <p className="text-gray-700 dark:text-slate-300 flex items-center gap-1.5 min-w-0">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admin/clients/${appointment.client.id}`);
                      }}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline text-left truncate font-medium"
                    >
                      {appointment.client.name}
                    </button>
                    {isBirthdayToday(appointment.client.birthday) && (
                      <span className="text-yellow-500 text-base flex-shrink-0" title="Aniversário hoje!">
                        🎂
                      </span>
                    )}
                  </p>
                </div>

                {/* Client address (car companies) */}
                {company?.category === "car" && appointment.client.address && (
                  <button
                    type="button"
                    onClick={() => openGoogleMaps(appointment.client.address!)}
                    className="flex items-start gap-2 w-full text-left rounded-lg px-2 py-1.5 -mx-2 hover:bg-blue-50 dark:hover:bg-slate-700 transition-colors group"
                    title="Abrir endereço no Google Maps"
                  >
                    <span className="text-base flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform">
                      📍
                    </span>
                    <span className="text-sm text-gray-600 dark:text-slate-400 group-hover:text-blue-700 dark:group-hover:text-blue-300 line-clamp-2">
                      {appointment.client.address}
                    </span>
                  </button>
                )}

                {/* Price */}
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-yellow-100 dark:bg-yellow-900/50 rounded-full flex items-center justify-center">
                    <Icons.Money sx={{ fontSize: 16 }} className="text-yellow-600 dark:text-yellow-400" />
                  </div>
                  <p className="font-semibold text-lg text-gray-600 dark:text-slate-200">
                    {formatCurrency(appointment.price)}
                  </p>
                </div>

                {/* Payment */}
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center">
                    <Icons.Payment sx={{ fontSize: 16 }} className="text-green-600 dark:text-green-400" />
                  </div>
                  {appointment.payment ? (
                    <button
                      onClick={() => {
                        setAppointmentForPayment(appointment);
                        setPaymentModalOpen(true);
                      }}
                      className={`px-2 py-1 rounded-full text-xs font-medium border cursor-pointer hover:opacity-80 transition-opacity ${getPaymentMethodColor(
                        appointment.payment
                      )}`}
                      title="Clique para atualizar pagamento"
                    >
                      {appointment.payment}
                    </button>
                  ) : (
                    <ActionButton
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setAppointmentForPayment(appointment);
                        setPaymentModalOpen(true);
                      }}
                      className="text-xs px-2 py-1"
                    >
                      <Icons.Payment sx={{ fontSize: 14 }} className="mr-1" /> Pagar
                    </ActionButton>
                  )}
                </div>

                {/* Description */}
                {appointment.service?.description && (
                  <div className="bg-gray-50 dark:bg-slate-900/50 rounded-lg p-3 mt-3">
                    <p className="text-sm text-gray-600 dark:text-slate-300">
                      {appointment.service.description}
                    </p>
                  </div>
                )}

                {/* Car Information Section */}
                {company?.category === "car" && (
                  <div className="bg-gray-50 dark:bg-slate-900/50 rounded-lg p-3">
                    {appointment.clientCar ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/clients/${appointment.client.id}/cars`);
                        }}
                        className="flex items-start gap-2 w-full text-left text-sm hover:opacity-90 transition-opacity"
                        title="Ver veículos do cliente"
                      >
                        <CarBrandLogo
                          brand={appointment.clientCar.brand}
                          className="flex-shrink-0 mt-0.5"
                        />
                        <div>
                          <div
                            className={`font-medium ${getCarEffortTextColor(
                              appointment.clientCar.effort
                            )}`}
                            title={appointment.clientCar.effort ? `Esforço: ${appointment.clientCar.effort}` : undefined}
                          >
                            {appointment.clientCar.model}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                            {appointment.clientCar.plate}
                          </div>
                        </div>
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          setSelectedAppointment(appointment);
                          setClientCarsModalOpen(true);
                        }}
                        className="w-full px-3 py-2 text-sm text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 rounded hover:bg-blue-50 dark:hover:bg-blue-950/40 hover:border-blue-300 transition-colors flex items-center justify-center gap-2"
                      >
                        <Icons.Car sx={{ fontSize: 16 }} className="text-orange-600" />
                        <span>Adicionar Veículo</span>
                      </button>
                    )}
                  </div>
                )}

                {/* Appointment notes */}
                {appointment.notes ? (
                  <div className="bg-amber-50 dark:bg-amber-950/40 rounded-lg p-3 border border-amber-100 dark:border-amber-800">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-amber-800 dark:text-amber-300 mb-1">
                          Observações
                        </p>
                        <p className="text-sm text-gray-700 dark:text-slate-300 whitespace-pre-wrap">
                          {appointment.notes}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => openNotesModal(appointment)}
                        className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded transition-colors flex-shrink-0"
                        title="Editar observações"
                      >
                        <Icons.Edit sx={{ fontSize: 16 }} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <ActionButton
                    variant="outline"
                    size="sm"
                    onClick={() => openNotesModal(appointment)}
                    className="w-full justify-center text-xs"
                  >
                    <Icons.Assignment sx={{ fontSize: 14 }} className="mr-1" /> Adicionar observações
                  </ActionButton>
                )}

                {isCarCompany && renderDeliveryTimes(appointment, () => openNotesModal(appointment))}
              </div>

              {/* Actions - always show area so Edit is visible for all non-cancelled (normal + responsive) */}
              {(appointment.status.toLowerCase() !== "cancelled" ||
                (onReactivate && appointment.status.toLowerCase() === "cancelled")) && (
                  <div className="px-4 pb-4 space-y-2">
                    {/* Show edit for all non-cancelled appointments */}
                    {appointment.status.toLowerCase() !== "cancelled" && (
                      <ActionButton
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setAppointmentToEdit(appointment);
                          setEditModalOpen(true);
                        }}
                        className="w-full justify-center"
                      >
                        <Icons.Edit sx={{ fontSize: 16 }} className="mr-1" /> Editar
                      </ActionButton>
                    )}
                    {onReactivate &&
                      appointment.status.toLowerCase() === "cancelled" && (
                        <ActionButton
                          variant="success"
                          size="sm"
                          icon={<Icons.Repeat sx={{ fontSize: 16 }} />}
                          onClick={() => onReactivate(appointment)}
                          className="w-full justify-center"
                        >
                          Reativar
                        </ActionButton>
                      )}
                    {/* Show cancel for non-finished, non-cancelled appointments */}
                    {!finished && (
                      <>
                        {onCancel &&
                          appointment.status.toLowerCase() !== "cancelled" && (
                            <ActionButton
                              variant="danger"
                              size="sm"
                              onClick={() => onCancel(appointment)}
                              className="w-full justify-center"
                            >
                              <Icons.Close sx={{ fontSize: 16 }} className="mr-1" /> Cancelar
                            </ActionButton>
                          )}
                      </>
                    )}
                  </div>
                )}


            </div>
          );
        })}
      </div>

      {/* Edit Appointment Modal */}
      {appointmentToEdit && company && (
        <CreateAppointmentModal
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setAppointmentToEdit(null);
          }}
          onSuccess={() => {
            setEditModalOpen(false);
            setAppointmentToEdit(null);
            onRefresh();
          }}
          selectedDate={appointmentToEdit.appointmentDate}
          editingAppointment={{
            id: appointmentToEdit.id,
            clientId: appointmentToEdit.clientId,
            serviceId: appointmentToEdit.serviceId,
            professionalId: appointmentToEdit.professionalId,
            appointmentDate: appointmentToEdit.appointmentDate,
            startTime: appointmentToEdit.startTime,
            price: appointmentToEdit.price,
            clientCarId: appointmentToEdit.clientCarId,
          }}
        />
      )}

      {/* Payment Modal */}
      {appointmentForPayment && (
        <UpdatePaymentModal
          isOpen={paymentModalOpen}
          onClose={() => {
            setPaymentModalOpen(false);
            setAppointmentForPayment(null);
          }}
          onSuccess={() => {
            setPaymentModalOpen(false);
            setAppointmentForPayment(null);
            onRefresh();
          }}
          appointment={appointmentForPayment}
        />
      )}

      {/* Notes Modal */}
      {appointmentForNotes && (
        <AppointmentNotesModal
          isOpen={notesModalOpen}
          onClose={() => {
            setNotesModalOpen(false);
            setAppointmentForNotes(null);
          }}
          appointmentId={appointmentForNotes.id}
          initialNotes={appointmentForNotes.notes}
          initialDeliveryStartTime={appointmentForNotes.deliveryStartTime}
          initialDeliveryEndTime={appointmentForNotes.deliveryEndTime}
          showDeliveryFields={isCarCompany}
          onSuccess={() => {
            setNotesModalOpen(false);
            setAppointmentForNotes(null);
            onRefresh();
          }}
        />
      )}

      {/* Client Cars Modal */}
      {selectedAppointment && (
        <ClientCarsModal
          isOpen={clientCarsModalOpen}
          onClose={() => {
            setClientCarsModalOpen(false);
            setSelectedAppointment(null);
          }}
          clientId={selectedAppointment.client.id}
          loading={updatingAppointment}
          onCarSelect={async (car, meta) => {
            if (!selectedAppointment) return;

            setUpdatingAppointment(true);
            try {
              const update: { clientCarId: number; price?: string } = {
                clientCarId: car.id,
              };
              if (meta?.appointmentPrice != null) {
                update.price = meta.appointmentPrice;
              }
              const response = await appointmentsApi.updateAppointment(
                selectedAppointment.id,
                update
              );

              if (response.success) {
                onRefresh();
                setClientCarsModalOpen(false);
                setSelectedAppointment(null);
              } else {
                console.error("Failed to update appointment:", response.error);
                // You could add a toast notification here
              }
            } catch (error) {
              console.error("Error updating appointment:", error);
            } finally {
              setUpdatingAppointment(false);
            }
          }}
          selectedCarId={selectedAppointment.clientCar?.id}
          serviceId={selectedAppointment.serviceId}
          serviceForPricing={selectedAppointment.service}
          formatServicePrice={formatCurrency}
        />
      )}
    </>
  );
};
